import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
// Import conditionnel pour éviter les dépendances circulaires
// import { useNotifications } from '../contexts/NotificationContext';
import { auth, db } from '../services/firebase/config';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithCustomToken
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { createOrUpdateUser, getUserProfile } from '../firebase/services/userService';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import authService from '../services/authService';
import { msalInstance, safeMsalAcquireToken, safeMsalSsoSilent, getSsoSilentRequest, loginRequest } from '../auth/msalConfig';
import { isAdminFromToken } from '../utils/azureRoles';
import { clearGraphClient } from '../services/graphClient';
import firestoreListenerManager from '../services/firestoreListenerManager';
const FIREBASE_FUNCTION_URL = 'https://us-central1-diotranscript.cloudfunctions.net/exchangeAzureTokenHttp';

const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const DEBUG_AUTH = !IS_PRODUCTION || localStorage.getItem('debug_auth') === 'true';

const authLog = (message, data = null) => {
  if (DEBUG_AUTH) {
    if (data) {
      console.log(`[AuthContext] ${message}`, data);
    } else {
      console.log(`[AuthContext] ${message}`);
    }
  }
};

// Création du contexte d'authentification
const AuthContext = createContext();

// Définition des états d'authentification
const AUTH_STATUS = {
  NONE: 'none',           // Pas de tentative d'authentification
  LOADING: 'loading',     // Authentification en cours
  PROCESSING: 'processing', // Traitement de l'authentification
  AZURE: 'azure',         // Authentifié via Azure AD
  FIREBASE: 'firebase',   // Authentifié directement via Firebase
  TEST: 'test',           // Mode test
  ERROR: 'error'          // Erreur d'authentification
};

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  return useContext(AuthContext);
};

// Fournisseur du contexte d'authentification
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authStatus, setAuthStatus] = useState(AUTH_STATUS.LOADING);
  const [debugInfo, setDebugInfo] = useState({});
  const [tokenRefreshInProgress, setTokenRefreshInProgress] = useState(false);
  const [lastTokenRefresh, setLastTokenRefresh] = useState(null);
  const [tokenRefreshTimer, setTokenRefreshTimer] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [lastRefreshTrigger, setLastRefreshTrigger] = useState(null);
  // Hook sûr pour les notifications - évite les dépendances circulaires
  const addSystemNotification = (message, type = 'info') => {
    // Fallback en cas d'absence du NotificationProvider
    console.log(`[${type.toUpperCase()}] ${message}`);
  };

  // Log avec trace de pile
  const logWithStack = (message, obj = null) => {
    // Décommenter la ligne suivante pour avoir la trace d'appel pour le débogage
    // const stack = new Error().stack;
  };

  // Mettre à jour les informations de débogage
  const updateDebugInfo = (info) => {
    setDebugInfo(prev => ({ ...prev, ...info }));
  };

  // Rafraîchir le token Firebase à partir du token Azure stocké
  const refreshFirebaseToken = async (source = 'unknown') => {
    if (tokenRefreshInProgress) {
      return false;
    }

    const now = Date.now();
    const MIN_REFRESH_INTERVAL = 5 * 60 * 1000;

    if (lastRefreshTrigger && (now - lastRefreshTrigger) < MIN_REFRESH_INTERVAL) {
      return false;
    }

    setLastRefreshTrigger(now);

    try {
      setTokenRefreshInProgress(true);
      
      // Récupérer les informations de l'utilisateur Azure du localStorage
      const azureUserJson = localStorage.getItem('azureTestUser');
      if (!azureUserJson) {
        addSystemNotification('❌ Aucun utilisateur Azure trouvé dans localStorage', 'error');
        return false;
      }
      
      const azureUser = JSON.parse(azureUserJson);
      
      // 🔄 CORRECTION : Obtenir un token Azure valide via authService
      const newToken = await authService.getValidAzureToken();
      if (!newToken) {
        addSystemNotification('❌ Impossible d\'obtenir un token Azure valide', 'error');
        redirectToLogin('Token Azure invalide');
        return false;
      }
      
      // Mettre à jour les informations du token dans azureUser et localStorage
      azureUser.azureAccessToken = newToken;
      azureUser.tokenTimestamp = Date.now();
      localStorage.setItem('azureTestUser', JSON.stringify(azureUser));
      
      // Appeler la fonction Cloud pour obtenir un nouveau token Firebase
      const functions = getFunctions();
      const exchangeAzureToken = httpsCallable(functions, 'exchangeAzureToken');
      
      try {
        const result = await exchangeAzureToken({
          azureToken: azureUser.azureAccessToken
        });
        
        if (!result.data || !result.data.firebaseToken) {
          addSystemNotification('❌ Réponse de la fonction sans token Firebase', 'error');
          return false;
        }
        
        // Mettre à jour l'utilisateur dans localStorage
        azureUser.firebaseToken = result.data.firebaseToken;
        localStorage.setItem('azureTestUser', JSON.stringify(azureUser));

        // 🔥 CORRECTION CORS : Fermer tous les listeners Firestore AVANT le refresh
        // Ceci évite que les listeners continuent avec l'ancienne session d'authentification
        firestoreListenerManager.closeAllListeners();

        // Authentifier avec Firebase
        await signInWithCustomToken(auth, result.data.firebaseToken);

        setLastTokenRefresh(new Date().getTime());

        // Nettoyer le cache du client Graph pour forcer l'utilisation du nouveau token
        clearGraphClient();

        // 🔥 CORRECTION CORS : Redémarrer tous les listeners avec le nouveau contexte d'auth
        firestoreListenerManager.restartAllListeners();

        // Nettoyer les flags d'erreur d'authentification
        localStorage.removeItem('auth_refresh_failed');
        localStorage.removeItem('auth_refresh_needed');
        localStorage.removeItem('auth_warning_shown');
        localStorage.setItem('token_refresh_attempts', '0');

        // Programmer le prochain rafraîchissement
        scheduleNextTokenRefresh();

        // Déclencher l'événement de rafraîchissement réussi
        window.dispatchEvent(new CustomEvent('firebase-token-refreshed'));

        return true;
      } catch (functionError) {
        addSystemNotification('❌ Erreur lors de l\'échange de token', 'error');
        
        // Si l'erreur est 401, c'est probablement que le token Azure est expiré
        if (functionError.message && (
            functionError.message.includes('401') || 
            functionError.message.includes('non autorisé') || 
            functionError.message.includes('unauthorized')
        )) {
          addSystemNotification('⚠️ Token Azure invalide ou expiré, redirection vers la page de connexion', 'error');
          redirectToLogin("Token Azure invalide");
        }
        
        return false;
      }
    } catch (error) {
      addSystemNotification('❌ Erreur lors du rafraîchissement du token:', 'error');
      return false;
    } finally {
      // Important: toujours réinitialiser l'état même en cas d'erreur
      setTimeout(() => {
        setTokenRefreshInProgress(false);
      }, 2000); // Petit délai pour éviter une nouvelle tentative immédiate
    }
  };

  // Programmer le prochain rafraîchissement automatique du token
  const scheduleNextTokenRefresh = () => {
    // Éviter les programmations multiples
    if (tokenRefreshTimer) {
      return;
    }

    // Programmer un rafraîchissement dans 45 minutes
    const refreshInterval = 45 * 60 * 1000; // 45 minutes en millisecondes
    
    const timer = setTimeout(async () => {
      try {
        const success = await refreshFirebaseToken();
        if (!success) {
          addSystemNotification('⚠️ Échec du rafraîchissement automatique, programmation d\'une nouvelle tentative', 'error');
          // Réinitialiser le timer avant de programmer une nouvelle tentative
          setTokenRefreshTimer(null);
          setTimeout(() => scheduleNextTokenRefresh(), 5 * 60 * 1000);
        }
      } catch (error) {
        addSystemNotification('❌ Erreur lors du rafraîchissement automatique:', 'error');
        // Réinitialiser le timer avant de programmer une nouvelle tentative
        setTokenRefreshTimer(null);
        setTimeout(() => scheduleNextTokenRefresh(), 5 * 60 * 1000);
      }
    }, refreshInterval);
    
    setTokenRefreshTimer(timer);
  };

  // Surveiller la connexion réseau et Firestore
  const setupConnectionMonitoring = () => {
    // Détecter si on est sur mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));

    // Surveiller la connexion réseau
    const handleOnline = () => {
      setConnectionStatus('online');

      // Tenter un rafraîchissement si on était hors ligne
      if (currentUser && currentUser.isAzureUser) {
        const lastRefresh = lastTokenRefresh || 0;
        const minutesPassed = (new Date().getTime() - lastRefresh) / (1000 * 60);

        if (minutesPassed > 30) {
          refreshFirebaseToken('network-online');
        }
      }
    };

    const handleOffline = () => {
      addSystemNotification('⚠️ Connexion réseau perdue', 'error');
      setConnectionStatus('offline');
    };

    // Surveiller la visibilité de la page (important pour mobile)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Sauvegarder le timestamp quand l'app passe en arrière-plan
        localStorage.setItem('app_background_time', Date.now().toString());
      } else {
        // Vérifier si on doit rafraîchir le token au retour
        const backgroundTime = parseInt(localStorage.getItem('app_background_time') || '0');
        const backgroundDuration = Date.now() - backgroundTime;
        const lastRefresh = lastTokenRefresh || 0;
        const timeSinceLastRefresh = Date.now() - lastRefresh;

        // Si l'app était en arrière-plan plus de 30 minutes OU
        // si le dernier refresh date de plus de 45 minutes
        if (backgroundDuration > 30 * 60 * 1000 || timeSinceLastRefresh > 45 * 60 * 1000) {
          if (currentUser && currentUser.isAzureUser) {
            setTimeout(() => {
              refreshFirebaseToken('visibility-change');
            }, 1000);
          }
        }

        // Reprogrammer le timer s'il n'existe plus (peut être suspendu en arrière-plan)
        if (!tokenRefreshTimer && currentUser && currentUser.isAzureUser) {
          scheduleNextTokenRefresh();
        }

        // Nettoyer le timestamp de passage en arrière-plan
        localStorage.removeItem('app_background_time');
      }
    };

    // Surveiller les changements de focus de la fenêtre (pour desktop)
    const handleFocus = () => {
      // Similaire à visibilitychange mais pour les cas où visibilitychange ne fonctionne pas
      if (!isMobile) {
        const lastRefresh = lastTokenRefresh || 0;
        const timeSinceLastRefresh = Date.now() - lastRefresh;

        if (timeSinceLastRefresh > 45 * 60 * 1000 && currentUser && currentUser.isAzureUser) {
          refreshFirebaseToken('window-focus');
        }
      }
    };

    // Ajouter les écouteurs d'événements
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Retourner la fonction de nettoyage
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  };
  
  // Rediriger vers la page de connexion
  const redirectToLogin = (reason) => {
    // Sauvegarder la raison de la déconnexion
    localStorage.setItem('auth_redirect_reason', reason || 'Session expirée');
    
    // Supprimer l'utilisateur Azure du localStorage
    localStorage.removeItem('azureTestUser');
    
    // 🔒 CRITIQUE : Nettoyer le cache du client Microsoft Graph pour éviter 
    // qu'un utilisateur B utilise les données du compte A
    clearGraphClient();
    
    // Déconnecter l'utilisateur Firebase
    if (auth.currentUser) {
      firebaseSignOut(auth).catch(e => addSystemNotification('Erreur déconnexion:', 'error'));
    }
    
    // Mise à jour de l'état
    setCurrentUser(null);
    setAuthStatus(AUTH_STATUS.NONE);
    
    // Rediriger après un court délai
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  // useEffect 1: Initialisation de l'authentification au démarrage
  // Exécuté une seule fois au montage, ne doit JAMAIS avoir de dépendances qui changent
  // pour éviter la réinitialisation complète de l'auth
  useEffect(() => {
    (async () => {

      logWithStack('Initialisation du contexte d\'authentification...');
      updateDebugInfo({ authContextInitialized: true });

      // --- Gestion du résultat de loginRedirect MSAL (SSO sans popup) ---

      // Détecter si on est dans une vraie redirection MSAL
      const urlParams = new URLSearchParams(window.location.search);
      const urlHash = window.location.hash;
      const hasMsalParams = urlParams.has('code') || urlParams.has('state') || urlParams.has('error') ||
                           urlHash.includes('code=') || urlHash.includes('state=') || urlHash.includes('error=');

      // Protection contre le double montage (React StrictMode)
      const msalHandledKey = 'msal_redirect_handled';
      const msalHandledTimestamp = sessionStorage.getItem(msalHandledKey);
      const now = Date.now();

      // Si déjà traité il y a moins de 2 secondes, skip
      if (msalHandledTimestamp && (now - parseInt(msalHandledTimestamp)) < 2000) {
        return;
      }

      // Détection de boucle de redirection (seulement si vraie redirection MSAL)
      if (hasMsalParams) {
        const redirectCount = parseInt(sessionStorage.getItem('msal_redirect_count') || '0');

        if (redirectCount > 3) {

          // Utiliser le nettoyage sélectif au lieu de tout supprimer
          const cleanedCount = authService.cleanCorruptedMSALCache();

          // Réinitialiser les compteurs
          sessionStorage.removeItem('msal_redirect_count');
          sessionStorage.setItem('msal_cleanup_done', 'true');

          addSystemNotification(`🔴 Boucle de redirection détectée - ${cleanedCount} éléments corrompus nettoyés. Veuillez vous reconnecter.`, 'error');
          return;
        }

        // Incrémenter seulement pour les vraies redirections MSAL
        sessionStorage.setItem('msal_redirect_count', (redirectCount + 1).toString());
      } else {
        // Réinitialiser le compteur si pas de redirection
        sessionStorage.removeItem('msal_redirect_count');
      }

      try {
        const redirectResult = await msalInstance.handleRedirectPromise();

        if (redirectResult) {
          sessionStorage.removeItem('msal_redirect_count');

          let accessToken = redirectResult.accessToken;
          const account = redirectResult.account;
          if (!accessToken && account) {
            try {
              const tokenResp = await safeMsalAcquireToken({ ...loginRequest, account });
              accessToken = tokenResp?.accessToken;
            } catch (acqErr) {
              addSystemNotification('❌ Impossible d\'obtenir un token après redirect:', 'error');
            }
          }
          if (accessToken) {
            const response = await fetch(FIREBASE_FUNCTION_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ azureToken: accessToken })
            });
            if (response.ok) {
              const data = await response.json();
              if (data?.firebaseToken) {
                const azureUser = {
                  uid: data.userProfile?.uid || `azure-${account.localAccountId}`,
                  azureAccessToken: accessToken,
                  azureIdToken: redirectResult.idToken,
                  azureAccount: account,
                  firebaseToken: data.firebaseToken,
                  userProfile: data.userProfile,
                  tokenTimestamp: Date.now()
                };

                // Stocker dans le format legacy pour compatibilité
                localStorage.setItem('azureTestUser', JSON.stringify(azureUser));

                // NOUVEAU : Aussi mettre à jour via authService pour utiliser le nouveau système de stockage
                await authService.updateAzureToken(accessToken);

                // Sauvegarder le loginHint (email) pour le SSO silencieux
                if (account && account.username) {
                  localStorage.setItem('preferred_username', account.username);
                }

                try {
                  await signInWithCustomToken(auth, data.firebaseToken);
                  // signInWithAzure sera déclenché plus tard via le flux normal
                } catch (signErr) {
                  addSystemNotification('❌ Erreur signInWithCustomToken après redirect:', 'error');
                }
              }
            } else {
              addSystemNotification('❌ Échec échange token Firebase après redirect:', 'error');
            }
          }
        }
      } catch (redirectErr) {
        // Messages d'erreur spécifiques
        if (redirectErr?.errorCode === 'redirect_uri_mismatch') {
          addSystemNotification(`❌ Erreur Azure AD: URL de redirection non autorisée. Ajouter ${window.location.origin} dans Azure AD`, 'error');
        } else if (redirectErr?.errorCode === 'interaction_in_progress') {
          addSystemNotification('❌ Authentification déjà en cours. Cache corrompu - Nettoyage nécessaire', 'error');
        } else if (redirectErr?.errorCode === 'login_required') {
          addSystemNotification('❌ Authentification requise', 'error');
        } else {
          addSystemNotification(`❌ Erreur handleRedirectPromise: ${redirectErr?.errorMessage || redirectErr?.message || 'Erreur inconnue'}`, 'error');
        }
      } finally {
        // Toujours marquer le traitement comme fait pour éviter le double traitement
        sessionStorage.setItem(msalHandledKey, now.toString());

        // Si c'était une vraie redirection MSAL mais pas de résultat, réinitialiser quand même le compteur
        if (hasMsalParams) {
          sessionStorage.removeItem('msal_redirect_count');
        }
      }
      // --- Fin gestion redirect ---

      // ===== NOUVEAU : Tentative de connexion SSO silencieuse au démarrage =====
      // Si pas de redirection MSAL en cours, essayer le SSO silencieux pour récupérer le compte système
      if (!hasMsalParams) {
        // Récupérer le loginHint depuis localStorage (email sauvegardé lors de la dernière connexion)
        const loginHint = localStorage.getItem('preferred_username');

        if (loginHint) {
          try {
            const ssoRequest = getSsoSilentRequest(loginHint);
            const ssoResponse = await safeMsalSsoSilent(ssoRequest);

            if (ssoResponse && ssoResponse.accessToken) {

              // Échanger le token Azure contre un token Firebase
              const response = await fetch(FIREBASE_FUNCTION_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ azureToken: ssoResponse.accessToken })
              });

              if (response.ok) {
                const data = await response.json();
                if (data?.firebaseToken) {
                  const azureUser = {
                    uid: data.userProfile?.uid || `azure-${ssoResponse.account.localAccountId}`,
                    azureAccessToken: ssoResponse.accessToken,
                    azureIdToken: ssoResponse.idToken,
                    azureAccount: ssoResponse.account,
                    firebaseToken: data.firebaseToken,
                    userProfile: data.userProfile,
                    tokenTimestamp: Date.now()
                  };

                  // Stocker dans le format legacy pour compatibilité
                  localStorage.setItem('azureTestUser', JSON.stringify(azureUser));

                  // Mettre à jour via authService pour utiliser le nouveau système de stockage
                  await authService.updateAzureToken(ssoResponse.accessToken);

                  // Sauvegarder le loginHint pour les prochaines fois
                  if (ssoResponse.account?.username) {
                    localStorage.setItem('preferred_username', ssoResponse.account.username);
                  }

                  try {
                    await signInWithCustomToken(auth, data.firebaseToken);
                  } catch (signErr) {
                    console.error('❌ [SSO] Erreur signInWithCustomToken:', signErr);
                  }
                }
              }
            }
          } catch (ssoError) {
            // Le SSO silencieux peut échouer si l'utilisateur n'est pas connecté au niveau système
            // C'est normal, on continue simplement le flux normal
          }
        }
      }
      // ===== Fin tentative SSO silencieuse =====

      // Vérifier si nous avons un utilisateur Azure dans localStorage
      const checkAzureUser = async () => {
        const azureUserJson = localStorage.getItem('azureTestUser');
        if (azureUserJson) {
          try {
            logWithStack('Utilisateur Azure trouvé dans localStorage');
            const azureUser = JSON.parse(azureUserJson);

            // Essayer de rafraîchir le token Firebase si nécessaire
            const lastRefresh = lastTokenRefresh || 0;
            const hoursPassed = (new Date().getTime() - lastRefresh) / (1000 * 60 * 60);

            // Limiter le nombre de tentatives de rafraîchissement
            const refreshAttempts = parseInt(localStorage.getItem('token_refresh_attempts') || '0');

            if (hoursPassed > 0.5 && refreshAttempts < 3) { // Rafraîchir si plus de 30 minutes se sont écoulées
              // Incrémenter le compteur de tentatives
              localStorage.setItem('token_refresh_attempts', (refreshAttempts + 1).toString());

              const refreshSuccess = await refreshFirebaseToken();

              if (refreshSuccess) {
                // Réinitialiser le compteur de tentatives
                localStorage.setItem('token_refresh_attempts', '0');
              } else if (refreshAttempts >= 2) {
                // Après 3 tentatives, proposer une reconnexion
                addSystemNotification('⚠️ Échec du rafraîchissement après plusieurs tentatives', 'error');

                // Option 1: Rediriger vers la page de connexion
                // redirectToLogin("Échec de rafraîchissement");
                // return false;

                // Option 2: Continuer sans redirection mais avec un avertissement silencieux
                const warningState = localStorage.getItem('auth_warning_shown') || 'false';
                if (warningState !== 'true') {
                  localStorage.setItem('auth_warning_shown', 'true');

                  // Log système au lieu de notification utilisateur
                  try {
                    const systemLogService = (await import('../services/systemLogService.js')).default;
                    await systemLogService.logAuth(
                      azureUser?.uid || 'unknown',
                      azureUser?.email || 'unknown',
                      azureUser?.displayName || azureUser?.email?.split('@')[0] || 'Utilisateur',
                      'Session expirée: certaines fonctionnalités peuvent ne pas fonctionner correctement',
                      'warning',
                      {
                        tokenExpired: true,
                        warningShown: true,
                        tokenType: 'azure'
                      }
                    );
                  } catch (logError) {
                    console.warn('[AuthContext] Impossible de logger l\'expiration de session:', logError);
                  }

                  // Au lieu d'alert(), on peut utiliser un toast ou un banner discret
                  // Pour l'instant, on log simplement l'avertissement
                }
              }
            }

            // Aller chercher le profil Firestore pour enrichir l'utilisateur
            const userProfile = await getUserProfile(azureUser.uid);
            const enrichedUser = {
              ...azureUser,
              ...userProfile,
              isAdmin: userProfile?.isAdmin ?? false,
              isAzureUser: true
            };
            setCurrentUser(enrichedUser);
            setAuthStatus(AUTH_STATUS.AZURE);

            // Le rafraîchissement sera programmé par signInWithAzure si nécessaire

            updateDebugInfo({
              userFromLocalStorage: true,
              azureUid: azureUser.uid
            });
            return true;
          } catch (e) {
            addSystemNotification('Erreur lors du parsing de l\'utilisateur Azure', 'error');
            localStorage.removeItem('azureTestUser');
          }
        }
        return false;
        };

        // Vérifier d'abord Azure, puis Firebase
        const hasAzureUser = await checkAzureUser();

        if (!hasAzureUser) {
        // Effet pour observer les changements d'état d'authentification Firebase
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (user) {
            updateDebugInfo({ firebaseUserDetected: true, uid: user.uid });

            // Déterminer si c'est un utilisateur Azure (préfixe dans l'ID)
            const isAzureUser = user.uid.startsWith('azure-');
            setAuthStatus(isAzureUser ? AUTH_STATUS.AZURE : AUTH_STATUS.FIREBASE);

            // NOUVEAU : Vérification du token Azure au démarrage
            // Si utilisateur Azure mais pas de token en cache, tenter de le récupérer
            if (isAzureUser) {
              const azureToken = authService.getAzureToken();

              if (!azureToken) {
                try {
                  // Tenter de récupérer le token via MSAL
                  const recoveredToken = await authService.tryRecoverAzureToken();
                  if (!recoveredToken) {
                    // Log système pour tracer le problème
                    try {
                      const systemLogService = (await import('../services/systemLogService.js')).default;
                      await systemLogService.logAuth(
                        user.uid,
                        user.email,
                        user.displayName || user.email?.split('@')[0] || 'Utilisateur',
                        'Token Azure non disponible au démarrage - Certaines fonctionnalités peuvent être limitées',
                        'warning',
                        {
                          tokenRecoveryAttempted: true,
                          tokenRecovered: false
                        }
                      );
                    } catch (logError) {
                      console.warn('[AuthContext] Impossible de logger l\'absence de token:', logError);
                    }
                  }
                } catch (recoveryError) {
                  console.error('[AuthContext] ❌ Erreur lors de la récupération du token:', recoveryError.message);
                }
              }
            }

            // Récupérer les données du profil utilisateur depuis Firestore
            try {
              const userProfile = await getUserProfile(user.uid);

              // Si le profil n'existe pas, le créer avec des données de base
              if (!userProfile) {
                // Créer un profil avec des données de base
                const newProfile = {
                  uid: user.uid,
                  displayName: user.displayName || user.email.split('@')[0],
                  email: user.email,
                  photoURL: user.photoURL,
                  createdAt: new Date(),
                  lastLogin: new Date(),
                  updatedAt: new Date(),
                  isAzureUser,
                  settings: {
                    theme: 'light',
                    notifications: true,
                  }
                };

                // Créer le profil dans Firestore
                await createOrUpdateUser(user.uid, newProfile);

                // Récupérer le profil nouvellement créé
                const createdProfile = await getUserProfile(user.uid);

                const enrichedUser = {
                  ...user,
                  profile: createdProfile,
                  isAzureUser,
                  isAdmin: createdProfile?.isAdmin ?? false
                };

                setCurrentUser(enrichedUser);
              } else {
                // Le profil existe, enrichir l'utilisateur avec ces données
                const enrichedUser = {
                  ...user,
                  profile: userProfile,
                  isAzureUser,
                  isAdmin: userProfile?.isAdmin ?? false
                };

                // Vérifier si displayName est présent dans le profil
                if (!userProfile.displayName && user.displayName) {
                  // Mettre à jour le profil avec le displayName de l'authentification
                  await updateDoc(doc(db, 'users', user.uid), {
                    displayName: user.displayName,
                    updatedAt: new Date()
                  });

                  // Mettre à jour le profil local
                  enrichedUser.profile.displayName = user.displayName;
                }

                setCurrentUser(enrichedUser);
              }
            } catch (error) {
              // Gestion améliorée des erreurs de récupération de profil
              if (error.message?.includes('offline') ||
                  error.message?.includes('Failed to get document') ||
                  error.code === 'unavailable') {
                addSystemNotification('⚠️ Mode offline: utilisation de données utilisateur minimales', 'error');
              } else {
                addSystemNotification('❌ Erreur lors de la récupération du profil:', 'error');
              }

              // Continuer avec l'utilisateur Firebase de base
              setCurrentUser({
                ...user,
                isAdmin: false, // Par défaut
                offlineMode: error.message?.includes('offline') || error.code === 'unavailable'
              });
            }
          } else if (!hasAzureUser) {
            // Ne réinitialiser que si nous n'avons pas trouvé d'utilisateur Azure
            setAuthStatus(AUTH_STATUS.NONE);
            setCurrentUser(null);
          }

          setLoading(false);
        });

        return unsubscribe;
      } else {
        setLoading(false);
        return () => {}; // Fonction de nettoyage vide si nous utilisons un utilisateur Azure
      }
    })();

    // Fonction de nettoyage
    return () => {
      // Nettoyer le timer de rafraîchissement
      if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
      }
    };
  }, []); // ⚠️ IMPORTANT: Pas de dépendances! Sinon toute l'auth se réinitialise

  // useEffect 2: Monitoring de la connexion réseau et de la visibilité de la page
  // Séparé de l'initialisation pour éviter les réinitialisations involontaires
  useEffect(() => {
    const cleanupConnectionMonitoring = setupConnectionMonitoring();

    return () => {
      cleanupConnectionMonitoring();
    };
  }, []); // Pas de dépendances pour avoir des listeners permanents

  // Connexion avec Azure AD
  const signInWithAzure = async (azureUser = null) => {
    setAuthStatus(AUTH_STATUS.PROCESSING);
    
    try {
      // Si aucun utilisateur Azure n'est fourni, essayer de le récupérer du localStorage
      if (!azureUser) {
        const storedUser = localStorage.getItem('azureTestUser');
        if (storedUser) {
          azureUser = JSON.parse(storedUser);
        } else {
          addSystemNotification('❌ Aucun utilisateur Azure disponible dans localStorage', 'error');
          setAuthStatus(AUTH_STATUS.NONE);
          return null;
        }
      }

      // Vérifier l'âge du token Azure si présent
      if (azureUser.tokenTimestamp) {
        const now = new Date().getTime();
        const tokenAgeHours = (now - azureUser.tokenTimestamp) / (1000 * 60 * 60);
        
        if (tokenAgeHours > 24) {
          addSystemNotification('⚠️ Token Azure expiré (> 24h), redirection vers la page de connexion', 'error');
          redirectToLogin("Token Azure expiré");
          return null;
        }
      }
      
      // Si nous avons un token Firebase, l'utiliser pour l'authentification Firebase
      if (azureUser.firebaseToken) {
        
        try {
          // Utiliser le token personnalisé pour se connecter à Firebase
          const userCredential = await signInWithCustomToken(auth, azureUser.firebaseToken);
          setLastTokenRefresh(new Date().getTime());

          // Réinitialiser les compteurs d'erreur
          localStorage.setItem('token_refresh_attempts', '0');
          localStorage.removeItem('auth_warning_shown');
        } catch (firebaseError) {
          addSystemNotification('❌ Erreur lors de l\'authentification Firebase', 'error');
          addSystemNotification('⚠️ Code erreur:', 'error');
          addSystemNotification('⚠️ Message erreur:', 'error');
          
          // Gestion spécifique des erreurs temporaires de réseau
          if (firebaseError.code === 'auth/network-request-failed' || 
              firebaseError.code === 'unavailable' ||
              firebaseError.message?.includes('network')) {
            addSystemNotification('⚠️ Erreur réseau temporaire, tentative de continuation...', 'error');
            
            // Pour les erreurs de réseau, on continue sans interrompre le processus
            // L'utilisateur peut déjà être authentifié côté Firebase
            if (auth.currentUser) {
              // Continuer avec l'utilisateur existant
            } else {
              // Essayer plusieurs fois avec délais progressifs
              let retryCount = 0;
              const maxRetries = 3;
              let retrySuccess = false;
              
              while (retryCount < maxRetries && !retrySuccess) {
                retryCount++;
                const delayMs = retryCount * 1000; // 1s, 2s, 3s
                
                await new Promise(resolve => setTimeout(resolve, delayMs));
                
                try {
                  await signInWithCustomToken(auth, azureUser.firebaseToken);
                  setLastTokenRefresh(new Date().getTime());
                  localStorage.setItem('token_refresh_attempts', '0');
                  localStorage.removeItem('auth_warning_shown');
                  retrySuccess = true;
                } catch (retryError) {
                  addSystemNotification('⚠️ Tentative ${retryCount} échouée:', 'error');
                  if (retryCount === maxRetries) {
                    addSystemNotification('⚠️ Toutes les tentatives ont échoué, continuation en mode dégradé', 'error');
                    // Ne pas interrompre le processus, l'utilisateur peut toujours utiliser l'app
                  }
                }
              }
            }
          }
          // Si le token est expiré ou invalide, essayer de le rafraîchir
          else if (firebaseError.code === 'auth/invalid-custom-token' || 
                   firebaseError.code === 'auth/custom-token-mismatch' ||
                   firebaseError.message?.includes('expired')) {
            const refreshed = await refreshFirebaseToken();
            if (!refreshed) {
              addSystemNotification('⚠️ Impossible de rafraîchir le token, continuation en mode dégradé', 'error');
              // Ne pas interrompre le processus
            }
          } else {
            addSystemNotification('⚠️ Erreur d\'authentification', 'error');
            // Pour toute autre erreur, continuer sans interrompre
          }
        }
      } else {
        addSystemNotification('⚠️ Aucun token Firebase disponible, tentative de rafraîchissement', 'error');
        const refreshed = await refreshFirebaseToken();
        if (!refreshed) {
          addSystemNotification('⚠️ Impossible de rafraîchir le token, mode dégradé', 'error');
          
          // Si on n'a pas de token et qu'on ne peut pas en obtenir, rediriger vers la connexion
          if (!auth.currentUser) {
            redirectToLogin("Impossible de récupérer un token Firebase");
            return null;
          }
        }
      }
      
      // Utiliser l'ID unique de l'utilisateur Azure tel qu'il est dans le localStorage
      // L'ID devrait être de la forme 'azure-{azureId}' tel que créé par la fonction Cloud
      const userId = azureUser.uid;

      // Vérifier si le profil existe déjà dans Firestore
      const userRef = doc(db, 'users', userId);
      
      try {
        const userSnap = await getDoc(userRef);
        
        let userProfile;
        
        if (userSnap.exists()) {
          // Profil trouvé - récupérer les données
          userProfile = userSnap.data();

          // Mettre à jour le profil avec les dernières infos Azure si nécessaire
          if (userProfile.displayName !== azureUser.displayName || userProfile.email !== azureUser.email) {
            try {
              await updateDoc(userRef, {
                displayName: azureUser.displayName,
                email: azureUser.email,
                lastLogin: serverTimestamp(),
              });
              
              // Mettre à jour les données locales
              userProfile.displayName = azureUser.displayName;
              userProfile.email = azureUser.email;
            } catch (updateError) {
              addSystemNotification('❌ Erreur lors de la mise à jour du profil:', 'error');
            }
          }
        } else {
          // Aucun profil trouvé - créer un nouveau
          userProfile = {
            uid: userId,
            displayName: azureUser.displayName || azureUser.email.split('@')[0],
            email: azureUser.email,
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
            isAzureUser: true,
            // Valeurs par défaut pour un nouveau profil
            role: 'user',
            settings: {
              theme: 'light',
              notifications: true,
            }
          };
          
          await setDoc(userRef, userProfile);
        }
        
        // Déterminer si l'utilisateur est admin côté Azure
        const tokenAdmin = isAdminFromToken(azureUser.azureIdToken);

        // Enrichir l'objet utilisateur avec ses données Firestore
        const enrichedUser = {
          ...azureUser,
          ...userProfile,
          isAzureUser: true,
          isAdmin: userProfile?.isAdmin ?? tokenAdmin
        };
        
        // Nettoyer les flags d'erreur d'authentification lors d'une connexion réussie
        localStorage.removeItem('auth_refresh_failed');
        localStorage.removeItem('auth_refresh_needed');
        localStorage.removeItem('auth_warning_shown');
        localStorage.setItem('token_refresh_attempts', '0');
        
        // Mettre à jour le localStorage avec l'utilisateur enrichi
        localStorage.setItem('azureTestUser', JSON.stringify(enrichedUser));
        
        setCurrentUser(enrichedUser);
        setAuthStatus(AUTH_STATUS.AZURE);

        // IMPORTANT: Programmer le rafraîchissement automatique du token
        scheduleNextTokenRefresh();

        return enrichedUser;
      } catch (error) {
        addSystemNotification('❌ Erreur lors de la récupération du profil:', 'error');
        
        // Gestion spécifique des erreurs de mode offline
        if (error.message?.includes('offline') || 
            error.message?.includes('Failed to get document') ||
            error.code === 'unavailable') {
          addSystemNotification('⚠️ Mode offline détecté, continuation avec données minimales', 'error');
          
          // Créer un utilisateur minimal avec les données Azure disponibles
          const minimalUser = {
            ...azureUser,
            isAzureUser: true,
            isAdmin: false, // Par défaut, sera mis à jour quand la connexion reviendra
            offlineMode: true,
            profile: {
              uid: azureUser.uid,
              displayName: azureUser.displayName,
              email: azureUser.email,
              isAzureUser: true
            }
          };
          
          setCurrentUser(minimalUser);
          setAuthStatus(AUTH_STATUS.AZURE);
          
          // Note: rafraîchissement déjà programmé dans le cas normal
          
          return minimalUser;
        }
        
        // Pour les autres erreurs, essayer de continuer avec des données minimales
        addSystemNotification('⚠️ Erreur de profil, continuation avec données de base', 'error');
        const basicUser = {
          ...azureUser,
          isAzureUser: true,
          isAdmin: isAdminFromToken(azureUser.azureIdToken),
          profile: {
            uid: azureUser.uid,
            displayName: azureUser.displayName || 'Utilisateur',
            email: azureUser.email
          }
        };
        
        setCurrentUser(basicUser);
        setAuthStatus(AUTH_STATUS.AZURE);
        
        // Note: rafraîchissement déjà programmé dans le cas normal
        
        return basicUser;
      }
    } catch (error) {
      addSystemNotification('❌ Erreur lors de l\'authentification Azure', 'error');

      // Pour les erreurs de réseau, ne pas mettre le statut en erreur
      if (error.code === 'auth/network-request-failed' ||
          error.message?.includes('network') ||
          error.message?.includes('offline')) {
        addSystemNotification('⚠️ Erreur réseau lors de l\'authentification', 'error');
        
        // Essayer de continuer avec les données utilisateur locales
        if (azureUser) {
          const enrichedUser = {
            ...azureUser,
            isAzureUser: true,
            isAdmin: azureUser.isAdmin ?? false,
            offlineMode: true // Indicateur de mode dégradé
          };
          
          setCurrentUser(enrichedUser);
          setAuthStatus(AUTH_STATUS.AZURE);
          
          // Note: rafraîchissement déjà programmé dans le cas normal
          
          return enrichedUser;
        }
      }
      
      // Pour toute autre erreur, log mais ne pas interrompre complètement
      addSystemNotification('⚠️ Authentification en mode dégradé', 'error');
      setAuthStatus(AUTH_STATUS.NONE);
      return null;
    }
  };

  // Connexion classique avec Firebase
  const login = async (email, password) => {
    try {
      setAuthStatus(AUTH_STATUS.LOADING);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setAuthStatus(AUTH_STATUS.FIREBASE);
      return userCredential.user;
    } catch (error) {
      setError(error.message);
      setAuthStatus(AUTH_STATUS.ERROR);
      throw error;
    }
  };

  // Inscription avec Firebase
  const signup = async (email, password, displayName) => {
    try {
      setAuthStatus(AUTH_STATUS.LOADING);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Créer le profil utilisateur dans Firestore
      await createOrUpdateUser(userCredential.user.uid, {
        email,
        displayName,
        authProvider: 'email',
        photoURL: null
      });
      
      setAuthStatus(AUTH_STATUS.FIREBASE);
      return userCredential.user;
    } catch (error) {
      setError(error.message);
      setAuthStatus(AUTH_STATUS.ERROR);
      throw error;
    }
  };

  // Déconnexion
  const logout = async () => {
    try {
      // Nettoyer les timers avant la déconnexion
      if (tokenRefreshTimer) {
        clearTimeout(tokenRefreshTimer);
        setTokenRefreshTimer(null);
      }

      // Vérifier d'abord s'il y a un utilisateur Azure dans localStorage
      const azureUserJson = localStorage.getItem('azureTestUser');
      if (azureUserJson) {
        localStorage.removeItem('azureTestUser');
      }

      // Nettoyer les autres données d'authentification
      localStorage.removeItem('token_refresh_attempts');
      localStorage.removeItem('auth_warning_shown');
      localStorage.removeItem('auth_refresh_needed');
      localStorage.removeItem('auth_refresh_failed');

      // 🔒 CRITIQUE : Nettoyer le cache du client Microsoft Graph pour éviter
      // qu'un utilisateur B utilise les données du compte A
      clearGraphClient();
      
      // Déconnecter également l'utilisateur Firebase si présent
      if (auth.currentUser) {
        await firebaseSignOut(auth);
      }

      // Déconnexion Azure AD via redirect (pas de popup)
      try {
        await msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
      } catch (e) {
        addSystemNotification('Erreur logoutRedirect MSAL:', 'error');
      }
      
      // Réinitialiser l'état
      setCurrentUser(null);
      setAuthStatus(AUTH_STATUS.NONE);
      setLastTokenRefresh(null);
      
      // Nettoyer l'exposition sur window
      if (window.__authContext) {
        delete window.__authContext;
      }

      return true;
    } catch (error) {
      addSystemNotification('❌ Erreur lors de la déconnexion:', 'error');
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour rafraîchir les données du profil utilisateur
  const refreshUserProfile = async () => {
    if (!currentUser) return;

    try {
      // Vérifier si le token Firebase est toujours valide
      if (currentUser.isAzureUser) {
        const lastRefresh = lastTokenRefresh || 0;
        const hoursPassed = (new Date().getTime() - lastRefresh) / (1000 * 60 * 60);
        
        if (hoursPassed > 0.75) { // Plus de 45 minutes
          const refreshed = await refreshFirebaseToken();
          if (!refreshed) {
            addSystemNotification('❌ Impossible de rafraîchir le token Firebase', 'error');
            return null;
          }
        }
      }
      
      const userProfile = await getUserProfile(currentUser.uid);
      
      if (userProfile) {
        // Vérifier si les paramètres utilisateur existent et les initialiser si nécessaire
        if (!userProfile.settings) {
          // Mettre à jour le profil pour ajouter les paramètres par défaut
          const defaultSettings = {
            theme: 'light',
            notifications: true
          };
          
          // Ajouter les paramètres par défaut au profil
          await updateDoc(doc(db, 'users', currentUser.uid), {
            settings: defaultSettings,
            updatedAt: new Date()
          });
          
          // Mettre à jour le profil local
          userProfile.settings = defaultSettings;
        }
        
        // Si le displayName est absent dans le profil mais présent en local, le synchroniser
        if (!userProfile.displayName && currentUser.displayName) {
          try {
            await updateDoc(doc(db, 'users', currentUser.uid), {
              displayName: currentUser.displayName,
              updatedAt: new Date()
            });
            
            // Mettre à jour le profil local
            userProfile.displayName = currentUser.displayName;
          } catch (error) {
            addSystemNotification('Erreur lors de la synchronisation du displayName:', 'error');
          }
        }
        
        // Si c'est un utilisateur Azure, vérifier si azure_displayName existe et l'utiliser si nécessaire
        if (userProfile.isAzureUser && userProfile.azureId && !userProfile.displayName) {
          // Tenter de récupérer le nom d'affichage directement depuis le document Firestore
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          const userData = userDoc.data();
          
          if (userData && userData.displayName) {
            userProfile.displayName = userData.displayName;
          }
        }
        
        // Mettre à jour l'utilisateur avec les nouvelles données de profil
        const updatedUser = {
          ...currentUser,
          profile: userProfile,
          // Copier les propriétés importantes du profil au niveau racine pour faciliter l'accès
          displayName: userProfile.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || 'Utilisateur'
        };
        
        setCurrentUser(updatedUser);
        
        return updatedUser;
      }
    } catch (error) {
      addSystemNotification('Erreur lors du rafraîchissement du profil:', 'error');
    }
  };

  // Valeur à exposer dans le contexte avec useMemo pour éviter les re-renders inutiles
  const value = useMemo(() => ({
    currentUser,
    login,
    signup,
    logout,
    signInWithAzure,
    refreshFirebaseToken,
    isAuthenticated: !!currentUser,
    loading,
    authStatus,
    error,
    refreshUserProfile,
    debugInfo
  }), [currentUser, loading, authStatus, error, debugInfo]);

  // Rendre accessible le contexte d'authentification pour la gestion des erreurs
  // (permet aux services non-React d'accéder au contexte)
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__authContext = value;
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.__authContext = null;
      }
    };
  }, [value]);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider; 